import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { PRODUCT_CHANGE_VERDICTS } from "../status"
import { storeFreshnessSchema } from "./runs"

// ── Catalog-mirror freshness audit (`nova products freshness`) ───────────────
//
// A read-only per-store report answering "how current is this marketplace's
// catalog mirror against live Magento?" — the operator-facing surface over the
// same primitives run-reconciliation uses (`getStoreFreshness`) plus the sync's
// own Magento delta/snapshot queries. Counts are at the PARENT level (a
// configurable or a standalone simple — `COALESCE(config_sku, simple_sku)`),
// matching how Magento enumerates the catalog; `mirrorSimples` is the raw
// per-variant row count. WARN-only signal: it never mutates anything.

/** One master_category's mirror occupancy + how many of its parents Magento has
 *  edited since the last successful sync. `masterCategory` is null for mirror
 *  rows whose breadcrumb never mapped to a master category. */
export const productsFreshnessCategorySchema = z.object({
  masterCategory: z.string().nullable(),
  mirrorConfigs: z.number().int().nonnegative(),
  mirrorSimples: z.number().int().nonnegative(),
  changedSinceSync: z.number().int().nonnegative(),
})
export type ProductsFreshnessCategory = z.infer<typeof productsFreshnessCategorySchema>

export const productsFreshnessReportSchema = z.object({
  marketplace: marketplaceIdSchema,
  /** The Magento store the marketplace resolves to (drives every count). */
  store: z.string(),
  /** ISO instant the report was computed (the freshness age is measured to it). */
  generatedAt: z.string(),
  /** Run coverage: when this store's catalog was last SUCCESSFULLY synced. */
  freshness: storeFreshnessSchema,
  totals: z.object({
    /** Distinct parents in the mirror. */
    mirrorConfigs: z.number().int().nonnegative(),
    /** Raw variant rows in the mirror. */
    mirrorSimples: z.number().int().nonnegative(),
    /** Distinct parents live in Magento. */
    magentoParents: z.number().int().nonnegative(),
    /** Parents in Magento with no mirror row (never synced / newly created). */
    missing: z.number().int().nonnegative(),
    /** Mirror-present parents Magento edited since the last successful sync
     *  anchor. Newly-created parents (no mirror row) are excluded here and owned
     *  by `missing`, so the two totals never double-count the same parent. */
    changedSinceSync: z.number().int().nonnegative(),
  }),
  /** Per master_category mirror occupancy, ordered largest-mirror-first. Rows
   *  cover mirror-known parents only; because `totals.changedSinceSync` is
   *  likewise scoped to mirror-present parents, `changedSinceSync` per row sums
   *  exactly to `totals.changedSinceSync` (newly-created changed parents have no
   *  mirror row and so no known category — they surface under `totals.missing`). */
  byMasterCategory: z.array(productsFreshnessCategorySchema),
  /** Bounded example SKUs for triage; capped server-side. */
  samples: z.object({
    /** In Magento, absent from the mirror. */
    missing: z.array(z.string()),
    /** Present in the mirror but edited in Magento since the last sync. */
    stale: z.array(z.string()),
  }),
})
export type ProductsFreshnessReport = z.infer<typeof productsFreshnessReportSchema>

// ── Product-change detection report (issue #131) ───────────────────────────
//
// Outcome of one store's delta-tick change detection: verdict counts and the
// capped per-SKU action preview. The API attaches it to
// `SyncProductsReport.changes`; the CLI parses the heartbeat payload with the
// SAME schema, so builder and renderer cannot drift. Detail lists are CAPPED
// (payload rule) — counts are authoritative, `actions` is a preview. Fields
// default so a payload from an older API still parses.

const productChangeVerdictSchema = z.enum(PRODUCT_CHANGE_VERDICTS)

/** One actionable per-SKU line for the report: which SKU and why. `ignore`
 *  verdicts never surface here — they are counted, not listed. */
export const productChangeActionSchema = z.object({
  configSku: z.string(),
  verdict: productChangeVerdictSchema.exclude(["ignore"]),
  /** Normalized attribute names that matched a relist rule (empty for `new`). */
  changedAttributes: z.array(z.string()).default([]),
})
export type ProductChangeAction = z.infer<typeof productChangeActionSchema>

export const productChangeDetectionReportSchema = z.object({
  store: z.string(),
  /** Anchor the scan used (ledger anchor, or the sync's `since` on bootstrap;
   *  the CLAMPED value when `sinceClamped` is true). */
  since: z.string(),
  /** True when the bootstrap `since` was older than the guard window and got
   *  clamped — changes before the clamped anchor were NOT scanned. */
  sinceClamped: z.boolean().default(false),
  newCount: z.number().int().default(0),
  relistCount: z.number().int().default(0),
  ignoredCount: z.number().int().default(0),
  /** Capped per-SKU preview of the actionable (new/relist) verdicts. */
  actions: z.array(productChangeActionSchema).default([]),
  actionsTruncated: z.boolean().default(false),
  /** Non-fatal failures. Detection never fails the sync — problems surface
   *  here (and block the ledger anchor from advancing past them). */
  errors: z.array(z.string()).default([]),
})
export type ProductChangeDetectionReport = z.infer<typeof productChangeDetectionReportSchema>

// Outcome of one store's audit-log-gap check on the nightly full resnapshot
// (issue #311 MR 3) — the point of having a repair pass at all.
//
// The repair pass rewrites a row when its `content_hash` moved. This asks the
// second question: did the Amasty audit log KNOW? A parent whose mirror row moved
// but which the log never reported over the window since the previous repair is a
// SKU the delta tick would never have picked up — an audit-log health signal, not a
// listing decision, which is why it is reported and persisted to
// `product_audit_log_gaps` and deliberately NOT written to the
// `product_change_events` relist ledger (scope agreed on issue #311, 2026-08-05).
//
// One false negative is expected BY DESIGN: a change made between the 20:00 delta
// tick and the overnight repair IS in the log, so it is not flagged. A change with
// no audit entry at all is. That is the signal wanted.
//
// The API attaches this to `SyncProductsReport.auditGap`; the CLI parses it with the
// SAME schema, so builder and renderer cannot drift. `sample` is CAPPED (payload
// rule) — the counts are authoritative. Fields default so a payload from an older
// API still parses.
export const productAuditLogGapReportSchema = z.object({
  store: z.string(),
  /** Start of the window the log was asked about — the previous successful repair.
   *  Null when there is none yet, in which case the check is `skipped`. */
  windowStart: z.string().nullable().default(null),
  /** Parents whose mirror rows this pass actually rewrote. */
  movedParents: z.number().int().default(0),
  /** Of those, the ones dropped from the comparison because they carried a row with
   *  no `content_hash` — such a row writes unconditionally, so it says nothing about
   *  the audit log. Reported because it is the difference between "clean" and "not
   *  asked": on the first pass after the schema push this equals `movedParents` and
   *  `gapParents` is 0, which must not read as a clean night. */
  unhashedParents: z.number().int().default(0),
  /** Of those, the ones dropped because a `simple_sku` they own was also written
   *  under a DIFFERENT parent in this pass — a simple that is a variant of two
   *  configurables, whose two payloads overwrite each other every pass (issue #311
   *  MR 3, accepted churn). Those rewrites are the pass fighting itself, not the
   *  audit log missing a change. Separate from `unhashedParents` because the operator
   *  action differs: a catalog shape rather than a backfill that ends. */
  ambiguousParents: z.number().int().default(0),
  /** How many parents were actually compared against the log. **Authoritative** — the
   *  two quarantine counts above are independent and can overlap, so do not derive
   *  this by subtracting them from `movedParents`. */
  comparedParents: z.number().int().default(0),
  /** Of the `comparedParents`, the ones the audit log never reported ⇒ the log missed
   *  them. */
  gapParents: z.number().int().default(0),
  /** Capped preview of `gapParents`. */
  sample: z.array(z.string()).default([]),
  sampleTruncated: z.boolean().default(false),
  /** Set when the check was deliberately not run, naming why — today only "there is
   *  no previous repair to compare against". Null when it ran. A mirror still being
   *  hash-backfilled does NOT skip the store; those parents are subtracted and
   *  counted in `unhashedParents` instead. */
  skipped: z.string().nullable().default(null),
  /** Non-fatal failures. The check never fails the sync. */
  errors: z.array(z.string()).default([]),
})
export type ProductAuditLogGapReport = z.infer<typeof productAuditLogGapReportSchema>

// ── Presence diff outcome (issue #338) ──────────────────────────────────────
//
// What one store's COMPLETED full pass found that Magento no longer returns. At
// the end of the walk the pass anti-joins the simple SKUs it sent against the
// store's mirror rows and stamps `products.absent_since` on the difference.
//
// **Information only.** No reader filters on the column and nothing is deleted; a
// flagged row stays a live mirror row every consumer still sees. This report is
// the measurement, and the per-store count is what an operator watches.
//
// Absent on delta ticks and on a store whose pass failed — neither has a complete
// seen-set, and a diff against an incomplete one would flag the catalog. The API
// attaches it to `SyncProductsReport.absent`; the CLI parses it with the SAME
// schema, so builder and renderer cannot drift. Fields default so a payload from an
// older API still parses.
export const productAbsenceReportSchema = z.object({
  store: z.string(),
  /** Every mirror row the store holds — the base `candidates` is a share of. */
  storeRows: z.number().int().default(0),
  /** Rows the pass did not send and that were not already flagged. Equal to
   *  `flagged` on every pass that wrote; when `refused`, this is what it WOULD have
   *  written, which is the number that makes a refusal actionable. */
  candidates: z.number().int().default(0),
  /** Rows actually stamped. 0 when `refused`. Expected shape on prod: ~749 per store
   *  on the first run (the accumulated backlog), then single digits a night. */
  flagged: z.number().int().default(0),
  /** The ceiling fired — `candidates` exceeded `ceilingRatio` of `storeRows`, so
   *  NOTHING was written and the store's flags are exactly as the last good pass left
   *  them. It is the catastrophe brake (a Magento side that silently under-returned),
   *  not the monitoring. */
  refused: z.boolean().default(false),
  /** The share `candidates / storeRows` had to stay under (0.05). Carried rather
   *  than hardcoded in the renderer so the rendered line cannot drift from the
   *  constant that decided it. */
  ceilingRatio: z.number().default(0.05),
  /** Non-fatal failures. The diff never fails the sync — the mirror rows are
   *  committed by the time it runs. */
  errors: z.array(z.string()).default([]),
})
export type ProductAbsenceReport = z.infer<typeof productAbsenceReportSchema>
